import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { exportIssuesToExcel, Issue } from '../lib/api';
import { useIssues } from '../hooks/useApiQueries';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, 
  MagnifyingGlass, 
  Funnel,
  WarningCircle,
  Truck,
  House,
  Export
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

export default function IssuesPage() {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || 'all');
  
  // Build query params
  const queryParams: Record<string, unknown> = {};
  if (statusFilter && statusFilter !== 'all') queryParams.status = statusFilter;
  if (categoryFilter && categoryFilter !== 'all') queryParams.category = categoryFilter;
  
  // React Query hook
  const { data: issuesData = [], isLoading: loading } = useIssues(queryParams, true);
  
  // Filter issues by search locally (for instant feedback)
  const issues = search 
    ? issuesData.filter((issue: Issue) => 
        issue.title?.toLowerCase().includes(search.toLowerCase()) ||
        issue.description?.toLowerCase().includes(search.toLowerCase())
      )
    : issuesData;

  const handleExport = async (): Promise<void> => {
    setExporting(true);
    try {
      await exportIssuesToExcel(issues);
      toast.success('Export slutförd');
    } catch (error) {
      toast.error('Export misslyckades');
    } finally {
      setExporting(false);
    }
  };

  const getStatusBadge = (status: string | undefined): string => {
    const badges: Record<string, string> = {
      open: 'badge-neutral',
      in_progress: 'badge-warning',
      waiting: 'badge-warning',
      resolved: 'badge-success',
      closed: 'badge-neutral'
    };
    return badges[status || 'open'] || 'badge-neutral';
  };

  const getUrgencyColor = (level: number | undefined): string => {
    const colors: Record<number, string> = {
      1: 'bg-green-500',
      2: 'bg-lime-500',
      3: 'bg-yellow-500',
      4: 'bg-orange-500',
      5: 'bg-red-500'
    };
    return colors[level || 1] || 'bg-slate-300';
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="issues-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          {isAdmin ? t('all_issues') : t('my_issues')}
        </h1>
        <div className="flex gap-2">
          {isAdmin && issues.length > 0 && (
            <Button 
              variant="outline" 
              onClick={handleExport} 
              disabled={exporting}
              data-testid="export-issues-btn"
            >
              <Export size={18} className="mr-2" />
              {exporting ? 'Exporterar...' : 'Exportera'}
            </Button>
          )}
          <Link to="/issues/new">
            <Button className="btn-press" data-testid="create-issue-button">
              <Plus size={18} className="mr-2" />
              {t('new_issue')}
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card-flat">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlass 
              size={18} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" 
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('search')}
              className="pl-10"
              data-testid="issues-search-input"
            />
          </div>
          
          <div className="flex gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]" data-testid="status-filter">
                <Funnel size={16} className="mr-2 text-slate-400" />
                <SelectValue placeholder={t('status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('status')}</SelectItem>
                <SelectItem value="open">{t('open')}</SelectItem>
                <SelectItem value="in_progress">{t('in_progress')}</SelectItem>
                <SelectItem value="waiting">{t('waiting')}</SelectItem>
                <SelectItem value="resolved">{t('resolved')}</SelectItem>
                <SelectItem value="closed">{t('closed')}</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[140px]" data-testid="category-filter">
                <SelectValue placeholder={t('issue_category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('issue_category')}</SelectItem>
                <SelectItem value="plumbing">{t('plumbing')}</SelectItem>
                <SelectItem value="electrical">{t('electrical')}</SelectItem>
                <SelectItem value="heating">{t('heating')}</SelectItem>
                <SelectItem value="other">{t('other')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Issues List */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="card-flat animate-pulse">
              <div className="h-4 w-1/3 bg-slate-200 rounded mb-2" />
              <div className="h-3 w-2/3 bg-slate-200 rounded" />
            </div>
          ))}
        </div>
      ) : issues.length === 0 ? (
        <div className="card-flat">
          <div className="empty-state">
            <WarningCircle size={48} className="empty-state-icon" />
            <p className="text-slate-500">{t('no_results')}</p>
            <Link to="/issues/new" className="mt-4 inline-block">
              <Button variant="outline" size="sm">
                <Plus size={16} className="mr-2" />
                {t('new_issue')}
              </Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {issues.map((issue) => (
            <Link
              key={issue.id}
              to={`/issues/${issue.id}`}
              className={`issue-card block ${(issue.urgency_level ?? 0) >= 4 ? 'issue-card-emergency' : ''}`}
              data-testid={`issue-item-${issue.id}`}
            >
              <div className="flex items-start gap-4">
                {/* Urgency indicator */}
                <div className="flex flex-col items-center gap-1 pt-1">
                  <div className={`w-2 h-2 rounded-full ${getUrgencyColor(issue.urgency_level)}`} />
                  <div className="text-[10px] text-slate-400 font-mono">{issue.urgency_level}</div>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="text-sm font-medium text-slate-900">
                      {issue.title}
                    </h3>
                    <span className={getStatusBadge(issue.status)}>
                      {t(issue.status || 'open')}
                    </span>
                  </div>
                  
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                    {issue.description}
                  </p>
                  
                  <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
                    <span className="px-2 py-0.5 bg-slate-100 rounded-md">
                      {t(issue.category || 'other')}
                    </span>
                    {issue.assigned_type === 'supplier' ? (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md">
                        <Truck size={12} />
                        Leverantör
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-slate-50 text-slate-600 rounded-md">
                        <House size={12} />
                        Internt
                      </span>
                    )}
                    <span>{issue.created_at ? new Date(issue.created_at).toLocaleDateString() : '-'}</span>
                    {(issue.attachments?.length ?? 0) > 0 && (
                      <span>{issue.attachments?.length} {t('attachments')}</span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
